import { useMemo, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import {
  useReceivableDocuments,
  useCreateReceivableDocument,
  useUpdateReceivableDocument,
  useDeleteReceivableDocument,
} from "../../hooks";
import {
  PendingReceivableDocument,
} from "../../utils/types";
import {
  DocumentListItem,
  DocumentFormValues,
  generateTempDocumentId,
} from "./utils";
import { DocumentListItem as DocumentListItemComponent } from "./DocumentListItem";
import { DocumentCreateDialog } from "./DocumentCreateDialog";
import { DocumentEditDialog } from "./DocumentEditDialog";
import { DocumentDeleteDialog } from "./DocumentDeleteDialog";
import { ReceivableDocumentType } from "../../utils/types";

interface ReceivableDocumentsSectionProps {
  receivableId?: string;
  pendingDocuments?: PendingReceivableDocument[];
  onPendingDocumentCreate?: (document: PendingReceivableDocument) => void;
  onPendingDocumentUpdate?: (document: PendingReceivableDocument) => void;
  onPendingDocumentDelete?: (id: string) => void;
  isProcessing?: boolean;
  isLocked?: boolean;
}

export function ReceivableDocumentsSection({
  receivableId,
  pendingDocuments = [],
  onPendingDocumentCreate,
  onPendingDocumentUpdate,
  onPendingDocumentDelete,
  isProcessing = false,
  isLocked = false,
}: ReceivableDocumentsSectionProps) {
  const {
    documents,
    isLoading: loadingDocuments,
    isFetching: fetchingDocuments,
  } = useReceivableDocuments(receivableId);
  const {
    createDocument,
    isLoading: creatingDocument,
  } = useCreateReceivableDocument();
  const {
    updateDocument,
    isLoading: updatingDocument,
  } = useUpdateReceivableDocument();
  const {
    deleteDocument,
    isLoading: deletingDocument,
  } = useDeleteReceivableDocument();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DocumentListItem | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<DocumentListItem | null>(null);

  const hasReceivableId = Boolean(receivableId);

  const sortedExistingDocuments = useMemo(() => {
    return [...documents].sort((a, b) => {
      const aDate = new Date(a.createdAt).getTime();
      const bDate = new Date(b.createdAt).getTime();
      return bDate - aDate;
    });
  }, [documents]);

  const sortedPendingDocuments = useMemo(() => {
    return [...pendingDocuments].sort((a, b) => {
      const aDate = new Date(a.createdAt).getTime();
      const bDate = new Date(b.createdAt).getTime();
      return bDate - aDate;
    });
  }, [pendingDocuments]);

  const listItems: DocumentListItem[] = hasReceivableId
    ? sortedExistingDocuments.map((document) => ({ kind: "existing", document }))
    : sortedPendingDocuments.map((document) => ({ kind: "pending", document }));

  const isBusy = creatingDocument || updatingDocument || deletingDocument || isProcessing;

  const handleCreateSubmit = async (values: DocumentFormValues) => {
    if (isProcessing || isLocked) return;
    const description = values.description?.trim() ? values.description.trim() : undefined;
    const documentUrl = values.documentUrl?.trim() ? values.documentUrl.trim() : undefined;
    const file = values.file instanceof File ? values.file : undefined;

    if (!file && !documentUrl) {
      return;
    }

    if (hasReceivableId && receivableId) {
      try {
        await createDocument({
          receivableId,
          documentType: values.documentType,
          description,
          file,
          documentUrl,
        });
        setIsCreateDialogOpen(false);
      } catch (error) {
        console.error("Erro ao anexar documento do recebível:", error);
      }
      return;
    }

    const pendingDocument: PendingReceivableDocument = {
      id: generateTempDocumentId(),
      documentType: values.documentType,
      description,
      documentUrl,
      file,
      createdAt: new Date().toISOString(),
    };
    onPendingDocumentCreate?.(pendingDocument);
    setIsCreateDialogOpen(false);
  };

  const handleEditSubmit = async (values: DocumentFormValues) => {
    if (isProcessing || !editingItem) return;
    const description = values.description?.trim() ? values.description.trim() : undefined;
    const documentUrl = values.documentUrl?.trim() ? values.documentUrl.trim() : undefined;
    const file = values.file instanceof File ? values.file : undefined;

    if (editingItem.kind === "existing") {
      if (!receivableId) return;
      try {
        await updateDocument({
          id: editingItem.document.id,
          receivableId,
          documentType: values.documentType,
          description,
          file,
          documentUrl,
        });
        setIsEditDialogOpen(false);
        setEditingItem(null);
      } catch (error) {
        console.error("Erro ao atualizar documento do recebível:", error);
      }
      return;
    }

    const previous = editingItem.document;
    const updatedPending: PendingReceivableDocument = {
      ...previous,
      documentType: values.documentType,
      description,
      documentUrl,
      file: file ?? previous.file,
      updatedAt: new Date().toISOString(),
    };
    onPendingDocumentUpdate?.(updatedPending);
    setIsEditDialogOpen(false);
    setEditingItem(null);
  };

  const handleOpenEdit = (item: DocumentListItem) => {
    setEditingItem(item);
    setIsEditDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem) return;
    if (deleteItem.kind === "existing") {
      if (!receivableId) return;
      await deleteDocument({ id: deleteItem.document.id, receivableId });
    } else {
      onPendingDocumentDelete?.(deleteItem.document.id);
    }
    setDeleteItem(null);
  };

  return (
    <>
      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Documentos anexados</CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            Últimos envios aparecem primeiro.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-end gap-3">
            {hasReceivableId && fetchingDocuments && (
              <span className="flex items-center text-xs text-muted-foreground">
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                Atualizando
              </span>
            )}
            {isLocked && (
              <span className="text-xs font-medium text-amber-500">
                Recebíveis concluídos não aceitam novos documentos.
              </span>
            )}
            <Button
              type="button"
              size="sm"
              onClick={() => setIsCreateDialogOpen(true)}
              disabled={isBusy || isLocked}
            >
              <Plus className="mr-2 h-4 w-4" />
              Novo documento
            </Button>
          </div>

          {hasReceivableId && loadingDocuments && sortedExistingDocuments.length === 0 ? (
            <div className="flex items-center justify-center gap-2 rounded-md border border-dashed border-muted-foreground/40 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando documentos...
            </div>
          ) : listItems.length === 0 ? (
            <div className="flex items-center justify-center rounded-md border border-dashed border-muted-foreground/40 p-6 text-sm text-muted-foreground">
              Nenhum documento anexado ainda.
            </div>
          ) : (
            <ul className="space-y-2">
              {listItems.map((item) => (
                <DocumentListItemComponent
                  key={`${item.kind}-${item.document.id}`}
                  item={item}
                  onDelete={(item) => setDeleteItem(item)}
                  disabled={isBusy}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <DocumentCreateDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={handleCreateSubmit}
        disabled={isBusy}
        isLocked={isLocked}
      />

      <DocumentEditDialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) setEditingItem(null);
        }}
        document={editingItem}
        onSubmit={handleEditSubmit}
        disabled={isBusy}
      />

      <DocumentDeleteDialog
        open={Boolean(deleteItem)}
        onOpenChange={(open) => !open && setDeleteItem(null)}
        document={deleteItem}
        onConfirm={handleConfirmDelete}
        isDeleting={deletingDocument || isProcessing}
      />
    </>
  );
}

